import { FirebaseApp } from "firebase/app";
import {
  Auth,
  browserLocalPersistence,
  browserSessionPersistence,
  getAuth,
  indexedDBLocalPersistence,
  initializeAuth,
} from "firebase/auth";

/**
 * Initialise Firebase Auth WITHOUT a popup/redirect resolver.
 *
 * Why: `getAuth()` passes `popupRedirectResolver: browserPopupRedirectResolver`,
 * which eagerly loads the auth iframe from the configured authDomain. With a
 * custom authDomain that is a 92KB request off a separate origin, and Lighthouse
 * measured it at 1,174ms - the longest chain on the initial load. Almost nobody
 * in an anonymous session signs in, so that cost is paid for machinery the visit
 * never uses. Omitting the resolver defers the iframe until a popup sign-in is
 * actually attempted; LoginForm passes the resolver explicitly at that point.
 *
 * The persistence list is COPIED VERBATIM from getAuth()'s own default in
 * @firebase/auth 1.13.0 (indexedDB, then localStorage, then sessionStorage, in
 * that order). It is not a preference - `initializeAuth` has no default, so
 * getting this list wrong silently signs out every existing user on their next
 * visit. If you upgrade firebase, re-check getAuth()'s default before touching
 * this.
 */
export const initAuthWithoutPopupResolver = (app: FirebaseApp): Auth => {
  try {
    return initializeAuth(app, {
      persistence: [
        indexedDBLocalPersistence,
        browserLocalPersistence,
        browserSessionPersistence,
      ],
    });
  } catch {
    // Already initialised - React StrictMode's double-invoke in dev, an HMR
    // reload, or some other getAuth() winning the race. Fall back to the
    // instance that exists. Worst case we lose the optimisation for this
    // session; we never break auth over it.
    return getAuth(app);
  }
};
